import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, X } from "lucide-react";
import { api } from "@/lib/api";

const POLL_INTERVAL_MS = 3000;

// Shared AudioContext across renders — unlocked on first user interaction
let sharedCtx = null;
let ctxUnlocked = false;
function getCtx() {
  if (sharedCtx) return sharedCtx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  sharedCtx = new Ctx();
  return sharedCtx;
}
function unlockAudio() {
  if (ctxUnlocked) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  try {
    const buffer = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);
    src.start(0);
  } catch (_) {}
  ctxUnlocked = true;
}

/**
 * Polls `/api/driver/notifications/pending` every 8s for the connected driver.
 * Each new item triggers a chime + browser Notification + Sonner toast + a
 * floating in-app banner (mirrors HotelNotificationListener).
 */
export default function DriverNotificationListener({ driverUserId }) {
  const [banner, setBanner] = useState(null);
  const bannerTimer = useRef(null);
  const intervalRef = useRef(null);
  const inflightRef = useRef(false);

  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch (_) {}
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      unlockAudio();
      document.removeEventListener("click", handler);
      document.removeEventListener("keydown", handler);
      document.removeEventListener("touchstart", handler);
    };
    document.addEventListener("click", handler, { once: false });
    document.addEventListener("keydown", handler, { once: false });
    document.addEventListener("touchstart", handler, { once: false });
    return () => {
      document.removeEventListener("click", handler);
      document.removeEventListener("keydown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, []);

  useEffect(() => {
    if (!driverUserId) return undefined;

    const poll = async () => {
      if (inflightRef.current) return;
      if (document.hidden) return;
      inflightRef.current = true;
      try {
        const { data } = await api.get("/driver/notifications/pending");
        const items = data?.notifications || [];
        items.forEach((n) => handleNotification(n, setBanner, bannerTimer));
      } catch (_) {
        // silent
      } finally {
        inflightRef.current = false;
      }
    };

    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    const onVis = () => { if (!document.hidden) poll(); };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      clearInterval(intervalRef.current);
      clearTimeout(bannerTimer.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [driverUserId]);

  if (!banner) return null;

  // The in-app "Nouvelle course disponible" banner has been retired per
  // product feedback — push notifications + the dashboard list already
  // surface new rides, so the floating card was redundant noise. We still
  // run the poll above to keep the chime + vibrate hooks alive so the
  // driver gets an audible alert even with the app in foreground.
  return null;
  /* eslint-disable no-unreachable */
  return (
    <div
      className="fixed bottom-6 right-6 z-[9999] w-[360px] max-w-[90vw] overflow-hidden rounded-2xl border border-primary/20 bg-card shadow-2xl animate-in slide-in-from-right-5 fade-in duration-300"
      data-testid="driver-push-banner"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
          <BellRing className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-widest text-primary">Notification</p>
          <p className="mt-0.5 font-[Outfit] text-base font-medium leading-tight">{banner.title}</p>
          <p className="mt-1 text-sm text-muted-foreground">{banner.message}</p>
          <div className="mt-3">
            <Link
              to="/dashboard/driver"
              onClick={() => setBanner(null)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              data-testid="driver-push-banner-open"
            >
              Voir la course →
            </Link>
          </div>
        </div>
        <button
          onClick={() => setBanner(null)}
          className="flex-none rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
          aria-label="Fermer"
          data-testid="driver-push-banner-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function handleNotification(n, setBanner, bannerTimer) {
  const title = n.title || "Saor";
  const message = n.message || "";

  // 1) Audio chime — sound feedback for incoming course
  try { playChime(); } catch (_) {}

  // 2) In-app floating banner — single visible popup. Sonner toast and
  //    native browser Notification are intentionally NOT triggered here to
  //    avoid the previous "3 popups for 1 event" spam.
  setBanner({ title, message, bookingId: n.booking_id || null });
  clearTimeout(bannerTimer.current);
  bannerTimer.current = setTimeout(() => setBanner(null), 8000);
}

function playChime() {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});

  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.9;
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 6000;
  master.connect(filter).connect(ctx.destination);

  const notes = [
    { f: 987.77, t: 0.00, d: 0.55 },
    { f: 1318.51, t: 0.12, d: 0.60 },
    { f: 1760.00, t: 0.24, d: 0.80 },
  ];
  notes.forEach(({ f, t, d }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = f;
    gain.gain.setValueAtTime(0.0001, now + t);
    gain.gain.exponentialRampToValueAtTime(0.28, now + t + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + t + d);
    osc.connect(gain).connect(master);
    osc.start(now + t);
    osc.stop(now + t + d + 0.05);
  });
}
